import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Switch,
  FormControlLabel,
  Snackbar,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  AccountBalanceWallet as TopUpIcon,
  RemoveCircleOutline as WithdrawIcon,
} from "@mui/icons-material";
import { tradersService, dictionariesService } from "../../../services";
import { TraderBalance, Trader } from "../../../types/trader";
import { Currency } from "../../../types/common/currency";
import AnimatedPage from "../../../components/AnimatedPage";
import { useNotification } from "../../../contexts/NotificationContext";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import { canPerformAction, ActionKey } from "../../../auth/actionMatrix";

interface EditBalanceDialogProps {
  open: boolean;
  balance: TraderBalance | null;
  onClose: () => void;
  onSave: (balanceId: string, data: Partial<TraderBalance>) => Promise<void>;
}

const EditBalanceDialog: React.FC<EditBalanceDialogProps> = ({
  open,
  balance,
  onClose,
  onSave,
}) => {
  const [formData, setFormData] = useState<Partial<TraderBalance>>({
    available_balance: "",
    insurance_balance_usdt: "",
    blocked_balance_in: "",
    blocked_balance_out: "",
    trc_twenty_wallet: "",
  });

  useEffect(() => {
    if (balance) {
      setFormData({
        available_balance: balance.available_balance || "",
        insurance_balance_usdt: balance.insurance_balance_usdt || "",
        blocked_balance_in: balance.blocked_balance_in || "",
        blocked_balance_out: balance.blocked_balance_out || "",
        trc_twenty_wallet: balance.trc_twenty_wallet || "",
      });
    } else {
      setFormData({
        available_balance: "",
        insurance_balance_usdt: "",
        blocked_balance_in: "",
        blocked_balance_out: "",
        trc_twenty_wallet: "",
      });
    }
  }, [balance]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (balance) {
      const dataToSave = { ...formData };
      if (balance.trc_twenty_wallet_managed) {
        delete dataToSave.trc_twenty_wallet;
      }
      await onSave(balance.id, dataToSave);
      onClose();
    }
  };

  const handleChange =
    (field: keyof TraderBalance) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({
        ...prev,
        [field]: e.target.value,
      }));
    };

  const handleSwitchChange =
    (field: keyof TraderBalance) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({
        ...prev,
        [field]: e.target.checked,
      }));
    };

  const handleClose = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableRestoreFocus
      disableEnforceFocus
    >
      <DialogTitle>
        {balance ? "Редактировать баланс" : "Создать баланс"}
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Stack spacing={2}>
            <TextField
              label="Доступный баланс"
              type="number"
              value={formData.available_balance}
              onChange={handleChange("available_balance")}
              required
              fullWidth
            />
            <TextField
              label="Страховой баланс USDT"
              type="number"
              value={formData.insurance_balance_usdt}
              onChange={handleChange("insurance_balance_usdt")}
              required
              fullWidth
            />
            <TextField
              label="Заблокированный баланс входящий"
              type="number"
              value={formData.blocked_balance_in}
              onChange={handleChange("blocked_balance_in")}
              required
              fullWidth
            />
            <TextField
              label="Заблокированный баланс исходящий"
              type="number"
              value={formData.blocked_balance_out}
              onChange={handleChange("blocked_balance_out")}
              required
              fullWidth
            />
            <TextField
              label="TRC20 кошелек"
              value={formData.trc_twenty_wallet}
              onChange={handleChange("trc_twenty_wallet")}
              required
              fullWidth
              disabled={balance?.trc_twenty_wallet_managed}
              helperText={
                balance?.trc_twenty_wallet_managed
                  ? "Кошелёк создан автоматически и управляется через crypto_wallets"
                  : undefined
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Отмена</Button>
          <Button type="submit" variant="contained">
            Сохранить
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

interface CreateBalanceDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: Partial<TraderBalance>) => Promise<void>;
  traders: Trader[];
  currencies: Currency[];
}

const CreateBalanceDialog: React.FC<CreateBalanceDialogProps> = ({
  open,
  onClose,
  onSave,
  traders,
  currencies,
}) => {
  const [formData, setFormData] = useState<Partial<TraderBalance>>({
    trader_id: "",
    currency_id: "",
    available_balance: "0",
    insurance_balance_usdt: "0",
    blocked_balance_in: "0",
    blocked_balance_out: "0",
    trc_twenty_wallet: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSave(formData);
    onClose();
  };

  const handleChange =
    (field: keyof TraderBalance) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({
        ...prev,
        [field]: e.target.value,
      }));
    };

  const handleSelectChange =
    (field: "trader_id" | "currency_id") => (e: SelectChangeEvent) => {
      setFormData((prev) => ({
        ...prev,
        [field]: e.target.value,
      }));
    };

  const handleSwitchChange =
    (field: keyof TraderBalance) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({
        ...prev,
        [field]: e.target.checked,
      }));
    };

  const handleClose = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableRestoreFocus
      disableEnforceFocus
    >
      <DialogTitle>Создать баланс</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Stack spacing={2}>
            <FormControl fullWidth>
              <InputLabel>Трейдер</InputLabel>
              <Select
                value={formData.trader_id}
                onChange={handleSelectChange("trader_id")}
                label="Трейдер"
                required
              >
                {traders.map((trader) => (
                  <MenuItem key={trader.id} value={trader.id}>
                    {trader.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Валюта</InputLabel>
              <Select
                value={formData.currency_id}
                onChange={handleSelectChange("currency_id")}
                label="Валюта"
                required
              >
                {currencies.map((currency) => (
                  <MenuItem key={currency.id} value={currency.id}>
                    {currency.iso_code}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Доступный баланс"
              type="number"
              value={formData.available_balance}
              onChange={handleChange("available_balance")}
              required
              fullWidth
            />
            <TextField
              label="Страховой баланс USDT"
              type="number"
              value={formData.insurance_balance_usdt}
              onChange={handleChange("insurance_balance_usdt")}
              required
              fullWidth
            />
            <TextField
              label="Заблокированный баланс входящий"
              type="number"
              value={formData.blocked_balance_in}
              onChange={handleChange("blocked_balance_in")}
              required
              fullWidth
            />
            <TextField
              label="Заблокированный баланс исходящий"
              type="number"
              value={formData.blocked_balance_out}
              onChange={handleChange("blocked_balance_out")}
              required
              fullWidth
            />
            <TextField
              label="TRC20 кошелек"
              value={formData.trc_twenty_wallet}
              onChange={handleChange("trc_twenty_wallet")}
              required
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Отмена</Button>
          <Button type="submit" variant="contained">
            Создать
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

interface DeleteBalanceDialogProps {
  open: boolean;
  balance: TraderBalance | null;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}

const DeleteBalanceDialog: React.FC<DeleteBalanceDialogProps> = ({
  open,
  balance,
  onClose,
  onConfirm,
}) => {
  const { showNotification } = useNotification();

  const handleConfirm = async () => {
    try {
      await onConfirm();
      onClose();
    } catch (err) {
      console.error("Error deleting balance:", err);
      showNotification("Ошибка при удалении баланса", "error");
    }
  };

  const handleClose = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    onClose();
  };

  if (!balance) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableRestoreFocus
      disableEnforceFocus
    >
      <DialogTitle>Подтверждение удаления</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography>
            Вы действительно хотите удалить баланс {balance.trader_name} (
            {balance.currency?.iso_code})?
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Отмена</Button>
        <Button onClick={handleConfirm} variant="contained" color="error">
          Удалить
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface TopUpBalanceDialogProps {
  open: boolean;
  balance: TraderBalance | null;
  onClose: () => void;
  onConfirm: (amount: number, description?: string) => Promise<void>;
}

const TopUpBalanceDialog: React.FC<TopUpBalanceDialogProps> = ({
  open,
  balance,
  onClose,
  onConfirm,
}) => {
  const [amount, setAmount] = useState<string>("");
  const [description, setDescription] = useState<string>("");

  useEffect(() => {
    if (open) {
      setAmount("");
      setDescription("");
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsedAmount = Number(amount);
    if (!parsedAmount || parsedAmount <= 0) return;
    const normalizedDescription = description.trim();
    await onConfirm(parsedAmount, normalizedDescription || undefined);
    onClose();
  };

  const handleClose = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    onClose();
  };

  if (!balance) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableRestoreFocus
      disableEnforceFocus
    >
      <DialogTitle>Пополнить баланс</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Stack spacing={2}>
            <Typography>
              Пополнение баланса {balance.trader_name} (
              {balance.currency?.iso_code || "-"})
            </Typography>
            <TextField
              label="Сумма пополнения"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
              fullWidth
              inputProps={{
                min: 0.01,
                step: 0.01,
              }}
              helperText="Введите сумму больше 0"
            />
            <TextField
              label="Описание"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              multiline
              minRows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Отмена</Button>
          <Button
            type="submit"
            variant="contained"
            color="success"
            disabled={!Number(amount) || Number(amount) <= 0}
          >
            Пополнить
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

interface WithdrawBalanceDialogProps {
  open: boolean;
  balance: TraderBalance | null;
  onClose: () => void;
  onConfirm: (amount: number, description?: string) => Promise<void>;
}

const WithdrawBalanceDialog: React.FC<WithdrawBalanceDialogProps> = ({
  open,
  balance,
  onClose,
  onConfirm,
}) => {
  const [amount, setAmount] = useState<string>("");
  const [description, setDescription] = useState<string>("");

  useEffect(() => {
    if (open) {
      setAmount("");
      setDescription("");
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsedAmount = Number(amount);
    if (!parsedAmount || parsedAmount <= 0) return;
    const normalizedDescription = description.trim();
    await onConfirm(parsedAmount, normalizedDescription || undefined);
    onClose();
  };

  const handleClose = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    onClose();
  };

  if (!balance) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableRestoreFocus
      disableEnforceFocus
    >
      <DialogTitle>Списать баланс</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Stack spacing={2}>
            <Typography>
              Списание с баланса {balance.trader_name} (
              {balance.currency?.iso_code || "-"})
            </Typography>
            <TextField
              label="Сумма списания"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
              fullWidth
              inputProps={{
                min: 0.01,
                step: 0.01,
              }}
              helperText="Введите сумму больше 0"
            />
            <TextField
              label="Описание"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              multiline
              minRows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Отмена</Button>
          <Button
            type="submit"
            variant="contained"
            color="warning"
            disabled={!Number(amount) || Number(amount) <= 0}
          >
            Списать
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

interface TraderBalancesRef {
  handleOpenDialog: () => void;
}

const TraderBalances = forwardRef<TraderBalancesRef>((props, ref) => {
  const [balances, setBalances] = useState<TraderBalance[]>([]);
  const [traders, setTraders] = useState<Trader[]>([]);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedBalance, setSelectedBalance] = useState<TraderBalance | null>(
    null,
  );
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });
  const { showNotification } = useNotification();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [balanceToDelete, setBalanceToDelete] = useState<TraderBalance | null>(
    null,
  );
  const [topUpDialogOpen, setTopUpDialogOpen] = useState(false);
  const [balanceToTopUp, setBalanceToTopUp] = useState<TraderBalance | null>(
    null,
  );
  const [withdrawDialogOpen, setWithdrawDialogOpen] = useState(false);
  const [balanceToWithdraw, setBalanceToWithdraw] = useState<TraderBalance | null>(
    null,
  );
  const [selectedCurrency, setSelectedCurrency] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [balanceState, setBalanceState] = useState<"all" | "positive" | "zero">("all");
  const currentUser = useSelector((state: RootState) => state.auth.user);

  const canCreate = useMemo(
    () => canPerformAction(currentUser, ActionKey.TRADER_BALANCES_CREATE),
    [currentUser],
  );
  const canUpdate = useMemo(
    () => canPerformAction(currentUser, ActionKey.TRADER_BALANCES_UPDATE),
    [currentUser],
  );
  const canDelete = useMemo(
    () => canPerformAction(currentUser, ActionKey.TRADER_BALANCES_DELETE),
    [currentUser],
  );
  const canAddFunds = useMemo(
    () => canPerformAction(currentUser, ActionKey.TRADER_BALANCES_ADD_FUNDS),
    [currentUser],
  );

  const currencyOptions = useMemo(
    () =>
      Array.from(
        new Map(
          balances.map((b) => [
            b.currency_id,
            {
              currency_id: b.currency_id,
              iso_code: b.currency?.iso_code || b.currency_id,
            },
          ]),
        ).values(),
      ),
    [balances],
  );

  const filteredBalances = useMemo(() => {
    const needle = searchQuery.trim().toLowerCase();
    return balances.filter((balance) => {
      const matchesSearch = !needle || [balance.trader_name, balance.trader_id, balance.id, balance.currency?.iso_code].some((value) => value?.toLowerCase().includes(needle));
      const available = Number(balance.available_balance ?? 0);
      return matchesSearch && (selectedCurrency === "all" || balance.currency_id === selectedCurrency) && (balanceState === "all" || (balanceState === "positive" ? available > 0 : available === 0));
    });
  }, [balances, selectedCurrency, searchQuery, balanceState]);

  useImperativeHandle(ref, () => ({
    handleOpenDialog: () => {
      if (!canCreate) {
        showNotification("У вас нет прав для создания балансов", "error");
        return;
      }
      setCreateDialogOpen(true);
    },
  }));

  const fetchTraders = async () => {
    try {
      const tradersData = await tradersService.getTraders({ is_archive: false });
      setTraders(tradersData);
    } catch (err: any) {
      console.error("Error fetching traders:", err);
    }
  };

  const fetchCurrencies = async () => {
    try {
      const currenciesData = await dictionariesService.getCurrencies();
      setCurrencies(currenciesData);
    } catch (err: any) {
      console.error("Error fetching currencies:", err);
      setError(err.response?.data?.detail || "Ошибка при загрузке валют");
    }
  };

  const fetchBalances = async () => {
    try {
      const balancesData = await tradersService.getTraderBalances();
      setBalances(balancesData);
      setError(null);
    } catch (err: any) {
      console.error("Error fetching balances:", err);
      setError(err.message || "Ошибка при загрузке балансов");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBalance = async (data: Partial<TraderBalance>) => {
    if (!canCreate) {
      showNotification("У вас нет прав для создания балансов", "error");
      return;
    }
    try {
      await tradersService.createTraderBalance(data);
      await fetchBalances();
      showNotification("Баланс успешно создан", "success");
    } catch (err: any) {
      console.error("Error creating balance:", err);
      showNotification("Ошибка при создании баланса", "error");
    }
  };

  const handleEditBalance = async (
    balanceId: string,
    data: Partial<TraderBalance>,
  ) => {
    if (!canUpdate) {
      showNotification("У вас нет прав для редактирования балансов", "error");
      return;
    }
    try {
      await tradersService.updateTraderBalance(balanceId, data);
      await fetchBalances();
      showNotification("Баланс успешно обновлен", "success");
    } catch (err: any) {
      console.error("Error updating balance:", err);
      showNotification("Ошибка при обновлении баланса", "error");
    }
  };

  const handleDeleteBalance = async (balanceId: string) => {
    if (!canDelete) {
      showNotification("У вас нет прав для удаления балансов", "error");
      return;
    }
    try {
      await tradersService.deleteTraderBalance(balanceId);
      await fetchBalances();
      showNotification("Баланс успешно удален", "success");
    } catch (err: any) {
      console.error("Error deleting balance:", err);
      showNotification("Ошибка при удалении баланса", "error");
    }
  };

  const handleDeleteClick = (balance: TraderBalance) => {
    setBalanceToDelete(balance);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (balanceToDelete) {
      await handleDeleteBalance(balanceToDelete.id);
    }
  };

  const handleTopUpBalance = async (
    balanceId: string,
    amount: number,
    description?: string,
  ) => {
    if (!canAddFunds) {
      showNotification("У вас нет прав для пополнения балансов", "error");
      return;
    }

    try {
      await tradersService.addFundsToTraderBalance(balanceId, {
        amount,
        description,
      });
      await fetchBalances();
      showNotification("Баланс успешно пополнен", "success");
    } catch (err: any) {
      console.error("Error topping up balance:", err);
      showNotification("Ошибка при пополнении баланса", "error");
      throw err;
    }
  };

  const handleTopUpClick = (balance: TraderBalance) => {
    if (!canAddFunds) {
      showNotification("У вас нет прав для пополнения балансов", "error");
      return;
    }
    setBalanceToTopUp(balance);
    setTopUpDialogOpen(true);
  };

  const handleWithdrawBalance = async (
    balanceId: string,
    amount: number,
    description?: string,
  ) => {
    if (!canAddFunds) {
      showNotification("У вас нет прав для списания балансов", "error");
      return;
    }

    try {
      await tradersService.withdrawFundsFromTraderBalance(balanceId, {
        amount,
        description,
      });
      await fetchBalances();
      showNotification("Списание баланса выполнено", "success");
    } catch (err: any) {
      console.error("Error withdrawing from balance:", err);
      showNotification("Ошибка при списании баланса", "error");
      throw err;
    }
  };

  const handleWithdrawClick = (balance: TraderBalance) => {
    if (!canAddFunds) {
      showNotification("У вас нет прав для списания балансов", "error");
      return;
    }
    setBalanceToWithdraw(balance);
    setWithdrawDialogOpen(true);
  };

  useEffect(() => {
    const traderId = window.location.pathname.split("/").pop();
    if (traderId) {
      Promise.all([fetchBalances(), fetchTraders(), fetchCurrencies()]);
    }
  }, []);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box mt={2}>
        <Alert severity="error">
          {typeof error === "string" ? error : "Произошла ошибка"}
        </Alert>
      </Box>
    );
  }

  return (
    <AnimatedPage>
      <Box>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Typography variant="h5">Балансы трейдеров</Typography>
        </Box>

        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <TextField sx={{ flex: 1, minWidth: 280 }} label="Поиск по трейдеру, ID или валюте" value={searchQuery} onChange={(event) => setSearchQuery(event.target.value)} />
            <FormControl sx={{ minWidth: 180 }}><InputLabel>Валюта</InputLabel><Select value={selectedCurrency} label="Валюта" onChange={(event) => setSelectedCurrency(event.target.value)}><MenuItem value="all">Все</MenuItem>{currencyOptions.map((currency) => <MenuItem key={currency.currency_id} value={currency.currency_id}>{currency.iso_code}</MenuItem>)}</Select></FormControl>
            <FormControl sx={{ minWidth: 200 }}><InputLabel>Доступный баланс</InputLabel><Select value={balanceState} label="Доступный баланс" onChange={(event) => setBalanceState(event.target.value as typeof balanceState)}><MenuItem value="all">Все</MenuItem><MenuItem value="positive">Положительный</MenuItem><MenuItem value="zero">Нулевой</MenuItem></Select></FormControl>
            <Button sx={{ whiteSpace: "nowrap" }} onClick={() => { setSearchQuery(""); setSelectedCurrency("all"); setBalanceState("all"); }}>Сбросить</Button>
          </Stack>
        </Paper>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Трейдер</TableCell>
                  <TableCell>Валюта</TableCell>
                  <TableCell>Доступный баланс</TableCell>
                  <TableCell>Страховой баланс USDT</TableCell>
                  <TableCell>Заблокированный входящий</TableCell>
                  <TableCell>Заблокированный исходящий</TableCell>
                  <TableCell>Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredBalances.map((balance) => (
                  <TableRow key={balance.id}>
                    <TableCell>{balance.trader_name}</TableCell>
                    <TableCell>{balance.currency?.iso_code || "-"}</TableCell>
                    <TableCell>{balance.available_balance}</TableCell>
                    <TableCell>{balance.insurance_balance_usdt}</TableCell>
                    <TableCell>{balance.blocked_balance_in}</TableCell>
                    <TableCell>{balance.blocked_balance_out}</TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <Tooltip
                          title={
                            canAddFunds
                              ? "Пополнить баланс"
                              : "Недостаточно прав"
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              sx={{ color: "#2e7d32" }}
                              onClick={() => handleTopUpClick(balance)}
                              disabled={!canAddFunds}
                            >
                              <TopUpIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip
                          title={
                            canAddFunds
                              ? "Списать с баланса"
                              : "Недостаточно прав"
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              sx={{ color: "#ed6c02" }}
                              onClick={() => handleWithdrawClick(balance)}
                              disabled={!canAddFunds}
                            >
                              <WithdrawIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip
                          title={
                            canUpdate ? "Редактировать" : "Недостаточно прав"
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => {
                                if (!canUpdate) {
                                  showNotification(
                                    "У вас нет прав для редактирования балансов",
                                    "error",
                                  );
                                  return;
                                }
                                setSelectedBalance(balance);
                                setEditDialogOpen(true);
                              }}
                              sx={{ color: "#1976d2" }}
                              disabled={!canUpdate}
                            >
                              <EditIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip
                          title={canDelete ? "Удалить" : "Недостаточно прав"}
                        >
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteClick(balance)}
                              sx={{ color: "#d32f2f" }}
                              disabled={!canDelete}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <CreateBalanceDialog
          open={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
          onSave={handleCreateBalance}
          traders={traders}
          currencies={currencies}
        />

        <EditBalanceDialog
          open={editDialogOpen}
          balance={selectedBalance}
          onClose={() => {
            setEditDialogOpen(false);
            setSelectedBalance(null);
          }}
          onSave={handleEditBalance}
        />

        <DeleteBalanceDialog
          open={deleteDialogOpen}
          balance={balanceToDelete}
          onClose={() => setDeleteDialogOpen(false)}
          onConfirm={handleDeleteConfirm}
        />

        <TopUpBalanceDialog
          open={topUpDialogOpen}
          balance={balanceToTopUp}
          onClose={() => {
            setTopUpDialogOpen(false);
            setBalanceToTopUp(null);
          }}
          onConfirm={async (amount, description) => {
            if (!balanceToTopUp) return;
            await handleTopUpBalance(balanceToTopUp.id, amount, description);
          }}
        />

        <WithdrawBalanceDialog
          open={withdrawDialogOpen}
          balance={balanceToWithdraw}
          onClose={() => {
            setWithdrawDialogOpen(false);
            setBalanceToWithdraw(null);
          }}
          onConfirm={async (amount, description) => {
            if (!balanceToWithdraw) return;
            await handleWithdrawBalance(balanceToWithdraw.id, amount, description);
          }}
        />
      </Box>
    </AnimatedPage>
  );
});

export default TraderBalances;
